import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { UnifiedItemsView } from './UnifiedItemsView';
import { QuotationPage, FlightInfo, AccommodationInfo, TourInfo, TransportationInfo, RestaurantInfo, QuotationBlock } from './types';
import { Plane, Building, Map, ViewIcon, Box, Car, Utensils, LayoutList, Rows, ListChecks } from 'lucide-react';
import { useTabViewPreference } from '@/hooks/useTabViewPreference';
import { useToast } from '@/hooks/use-toast';

interface TabViewProps {
  page: QuotationPage;
  updatePage: (id: string, field: keyof QuotationPage, value: any) => void;
  // Funções para manipular voos
  addFlight: (pageId: string) => void;
  removeFlight: (flightId: string) => void;
  // Funções para manipular acomodações
  addAccommodation: (pageId: string) => void;
  removeAccommodation: (accommodationId: string) => void;
  // Funções para manipular passeios
  addTour: (pageId: string) => void;
  removeTour: (tourId: string) => void;
  // Funções para manipular traslados
  addTransportation: (pageId: string) => void;
  removeTransportation: (transportationId: string) => void;
  // Funções para manipular restaurantes
  addRestaurant: (pageId: string) => void;
  removeRestaurant: (restaurantId: string) => void;
  // Componentes necessários
  FlightManagerComponent: React.ReactNode;
  AccommodationManagerComponent: React.ReactNode;
  TourManagerComponent: React.ReactNode;
  TransportationManagerComponent: React.ReactNode;
  RestaurantManagerComponent: React.ReactNode;
  BlocksComponent: React.ReactNode;
  addBlock: (pageId: string) => void;
  updateBlock: (pageId: string, blockId: string, field: keyof QuotationBlock, value: any) => void;
  deleteBlock: (pageId: string, blockId: string) => void;
  hideTitles?: boolean;
  moveTourToPage?: (sourcePageId: string, tourId: string, targetPageId: string) => void;
  moveRestaurantToPage?: (sourcePageId: string, restaurantId: string, targetPageId: string) => void;
  moveAccommodationToPage?: (sourcePageId: string, accommodationId: string, targetPageId: string) => void;
  moveFlightEventToPage?: (sourcePageId: string, flightEventId: string, targetPageId: string) => void;
  moveAccommodationEventToPage?: (sourcePageId: string, accommodationEventId: string, targetPageId: string) => void;
  moveTransportationToPage?: (sourcePageId: string, transportationId: string, targetPageId: string) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
  moveBlockToPage?: (sourcePageId: string, blockId: string, targetPageId: string) => void;
  allPages?: QuotationPage[];
}

export function TabView({ 
  page, 
  updatePage, 
  addFlight,
  removeFlight,
  addAccommodation,
  removeAccommodation,
  addTour,
  removeTour,
  addTransportation,
  removeTransportation,
  addRestaurant,
  removeRestaurant,
  FlightManagerComponent,
  AccommodationManagerComponent,
  TourManagerComponent,
  TransportationManagerComponent,
  RestaurantManagerComponent,
  BlocksComponent,
  addBlock,
  updateBlock,
  deleteBlock,
  hideTitles = false,
  moveTourToPage,
  moveRestaurantToPage,
  moveAccommodationToPage,
  moveFlightEventToPage,
  moveAccommodationEventToPage,
  moveTransportationToPage,
  moveFlightToPage,
  moveBlockToPage,
  allPages = []
}: TabViewProps) {
  // Usar o hook compartilhado em vez do estado local
  const { activeTab, setActiveTab } = useTabViewPreference();
  const { toast } = useToast();

  // Debug log for props
  React.useEffect(() => {
    console.log('[TabView] Props antes de passar para UnifiedItemsView:', {
      hasMoveFlightEventToPage: Boolean(moveFlightEventToPage),
      hasMoveAccommodationEventToPage: Boolean(moveAccommodationEventToPage),
      hasMoveAccommodationToPage: Boolean(moveAccommodationToPage),
      hasMoveTransportationToPage: Boolean(moveTransportationToPage),
      allPagesLength: allPages?.length,
      moveFlightEventToPageType: typeof moveFlightEventToPage,
      moveAccommodationEventToPageType: typeof moveAccommodationEventToPage,
      moveAccommodationToPageType: typeof moveAccommodationToPage,
      moveTransportationToPageType: typeof moveTransportationToPage,
      funcionTestCall: typeof moveFlightEventToPage === 'function' 
        ? 'É possível chamar a função' 
        : 'Não é possível chamar a função'
    });
  }, [moveFlightEventToPage, moveAccommodationEventToPage, moveAccommodationToPage, moveTransportationToPage, allPages]);

  // Função para remover itens
  const removeItem = (itemId: string, type: 'flight' | 'accommodation' | 'tour' | 'transportation' | 'restaurant' | 'flight-event' | 'accommodation-event') => {
    switch (type) {
      case 'flight':
        removeFlight(itemId);
        break;
      case 'accommodation':
        removeAccommodation(itemId);
        break;
      case 'tour':
        removeTour(itemId);
        break;
      case 'transportation':
        removeTransportation(itemId);
        break;
      case 'restaurant':
        removeRestaurant(itemId);
        break;
      case 'flight-event':
        const updatedFlightEvents = (page.flightEvents || []).filter(event => event.id !== itemId);
        updatePage(page.id, 'flightEvents', updatedFlightEvents);
        break;
      case 'accommodation-event':
        const updatedAccommodationEvents = (page.accommodationEvents || []).filter(event => event.id !== itemId);
        updatePage(page.id, 'accommodationEvents', updatedAccommodationEvents);
        break;
    }
  };

  // Função para adicionar um evento de voo
  const handleAddFlightEvent = () => {
    const newFlightEvent = {
      id: `flight-event-${Date.now()}`,
      title: 'Novo Evento de Voo',
      flightId: '',
      isDeparture: true,
      globalOrder: (page.flightEvents || []).length,
      coordinates: null,        // Inicializado como null, será atualizado quando o flightId for definido
      _coordinates: null        // Campo adicional para garantir compatibilidade com o mapa
    };

    const updatedFlightEvents = [...(page.flightEvents || []), newFlightEvent];
    updatePage(page.id, 'flightEvents', updatedFlightEvents);
  };

  // Função para adicionar um evento de acomodação
  const handleAddAccommodationEvent = () => {
    const newAccommodationEvent = {
      id: `accommodation-event-${Date.now()}`,
      title: 'Novo Evento de Hospedagem',
      accommodationId: '',
      isCheckIn: true,
      globalOrder: (page.accommodationEvents || []).length
    };

    const updatedAccommodationEvents = [...(page.accommodationEvents || []), newAccommodationEvent];
    updatePage(page.id, 'accommodationEvents', updatedAccommodationEvents);
  };

  return (
    <Card className="mt-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full p-0 flex rounded-b-none rounded-t-lg">
          <TabsTrigger 
            value="categorized" 
            className="flex-1 py-3 border-b-2 rounded-none data-[state=active]:border-[#087a83] data-[state=active]:text-[#087a83]"
          >
            <span className="flex items-center gap-2">
              <LayoutList className="h-4 w-4" />
              Visualização por Categorias
            </span>
          </TabsTrigger>
          <TabsTrigger 
            value="unified" 
            className="flex-1 py-3 border-b-2 rounded-none data-[state=active]:border-[#087a83] data-[state=active]:text-[#087a83]"
          >
            <span className="flex items-center gap-2">
              <ListChecks className="h-4 w-4" />
              Visualização Unificada
            </span>
          </TabsTrigger>
        </TabsList>
        
        <CardContent className="p-4">
          <TabsContent value="categorized" className="mt-0 space-y-4">
            {/* Visualização por categorias (blocos, voos, acomodações, passeios separados) */}
            <Card className="shadow-sm border-l-4 border-l-[#fbf0e2]">
              <CardContent className="p-0">
                {!hideTitles && (
                  <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                    <Box className="h-5 w-5 text-[#e6a23c]" />
                    <h3 className="text-base font-medium">Blocos</h3>
                  </div>
                )}
                {BlocksComponent}
              </CardContent>
            </Card>
            
            {page.showFlights && (
              <Card className="shadow-sm border-l-4 border-l-blue-400">
                <CardContent className="p-0">
                  {!hideTitles && (
                    <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                      <Plane className="h-5 w-5 text-blue-600" />
                      <h3 className="text-base font-medium">Voos</h3>
                    </div>
                  )}
                  {FlightManagerComponent}
                </CardContent>
              </Card>
            )}
            
            {page.showAccommodations && (
              <Card className="shadow-sm border-l-4 border-l-green-400">
                <CardContent className="p-0">
                  {!hideTitles && (
                    <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                      <Building className="h-5 w-5 text-green-600" />
                      <h3 className="text-base font-medium">Hospedagens</h3>
                    </div>
                  )}
                  {AccommodationManagerComponent}
                </CardContent>
              </Card>
            )}
            
            {page.showTours && (
              <Card className="shadow-sm border-l-4 border-l-amber-400">
                <CardContent className="p-0">
                  {!hideTitles && (
                    <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                      <Map className="h-5 w-5 text-amber-600" />
                      <h3 className="text-base font-medium">Tours</h3>
                    </div>
                  )}
                  {TourManagerComponent}
                </CardContent>
              </Card>
            )}
            
            {page.showTransportations && (
              <Card className="shadow-sm border-l-4 border-l-blue-400">
                <CardContent className="p-0">
                  {!hideTitles && (
                    <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                      <Car className="h-5 w-5 text-blue-600" />
                      <h3 className="text-base font-medium">Traslados</h3>
                    </div>
                  )}
                  {TransportationManagerComponent}
                </CardContent>
              </Card>
            )}
            
            {page.showRestaurants && (
              <Card className="shadow-sm border-l-4 border-l-purple-400">
                <CardContent className="p-0">
                  {!hideTitles && (
                    <div className="flex items-center gap-2 mb-3 p-4 pb-0">
                      <Utensils className="h-5 w-5 text-purple-600" />
                      <h3 className="text-base font-medium">Restaurantes</h3>
                    </div>
                  )}
                  {RestaurantManagerComponent}
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="unified" className="mt-0">
            {/* Visualização unificada (todos os itens juntos) */}
            <UnifiedItemsView
              page={page}
              updatePage={updatePage}
              addBlock={addBlock}
              updateBlock={updateBlock}
              deleteBlock={deleteBlock}
              removeItem={removeItem}
              addFlight={addFlight}
              addAccommodation={addAccommodation}
              addTour={addTour}
              addTransportation={addTransportation}
              addRestaurant={addRestaurant}
              addFlightEvent={handleAddFlightEvent}
              addAccommodationEvent={handleAddAccommodationEvent}
              moveTourToPage={moveTourToPage}
              moveRestaurantToPage={moveRestaurantToPage}
              moveAccommodationToPage={moveAccommodationToPage}
              moveFlightEventToPage={moveFlightEventToPage}
              moveAccommodationEventToPage={moveAccommodationEventToPage}
              moveTransportationToPage={moveTransportationToPage}
              moveFlightToPage={moveFlightToPage}
              moveBlockToPage={moveBlockToPage}
              allPages={allPages}
            />
          </TabsContent>
        </CardContent>
      </Tabs>
    </Card>
  );
} 